<template>
  <div class="medication-prescription">
    <v-row>
      <v-col
        class="
          sm-12
          md-6
          v-patient-overview-medication-prescription__open-decision-support
        "
      >
        <v-btn
          class="mr-6 mb-2"
          text
          color="primary"
          x-large
          @click="printPrescription('PATIENT')"
        >
          <smart-icon
            class="mr-2"
            icon="mdi-printer-outline"
            :iconColor="$color.coro['primary-500']"
          />
          {{ $ucfirst($t("dss.printPatient")) }}
        </v-btn>
        <v-btn
          class="mr-6 mb-2"
          text
          color="primary"
          x-large
          @click="printPrescription('GP')"
        >
          <smart-icon
            class="mr-2"
            icon="mdi-printer-outline"
            :iconColor="$color.coro['primary-500']"
          />
          {{ $ucfirst($t("dss.printCaregiver")) }}
        </v-btn>
        <open-d-s-s-button color='primary' icon="mdi-folder-outline"></open-d-s-s-button>
      </v-col>
    </v-row>
    <div
      class="v-patient-overview-medication-prescription"
      v-disable-all="$store.getters['user/hasReadOnlyAccess']"
    >
      <v-row>
        <v-col class="lg-6 sm-12 ma-2 border">
          <v-row class="border-bottom pb-2">
            <v-col cols="12" class="d-flex align-center">
              <smart-icon
                icon="mdi-heart-outline"
                :iconColor="$color.coro.gray"
                class="mr-2 d-flex"
              />
              <span class="v-patient-overview-medication-prescription__current-prescription">
                {{ $ucfirst($t('dss.currentPrescription')) }}
              </span>
            </v-col>
          </v-row>
          <div v-if="medicationSchedules && medicationSchedules.length">
            <div
              v-for="schedule in medicationSchedules"
              :key="schedule.id"
            >
              <medication-row
                v-if="!schedule.deleted || (schedule.deleted && schedule.wasDeletedByTypesSinceLastSDM)"
                :key="schedule.id"
                :schedule="schedule"
                :medicationClasses="medicationClasses"
                :allSelectedMedicationClasses="allSelectedMedicationClasses"
                @updatedClasses="updateClasses"
                @click:informationicon="activateMedication"
                :selected="isSelectedMedication(schedule)"
                :showReason="false"
              />
            </div>
          </div>
          <div v-else class="pt-3">
            <p class="mb-0">{{ $ucfirst($t('pdf.medicationPrescription.patientHasNoPrescriptions', {catalog:'messages'}))}}</p>
          </div>
        </v-col>

        <v-col
          class="lg-6 sm-12 ma-2 border py-3 px-5"
          v-if="selectedMedicationItem"
        >
          <v-row>
            <v-col cols="11">
              <h3>{{ selectedMedicationItem.schedule.medication.name }}</h3>
            </v-col>
            <v-col cols="1">
              <span
                class="v-patient-overview-medication-prescription__close-icon"
                @click="activateMedication(null)"
              >
                <smart-icon
                  icon="mdi-close-circle-outline"
                  size="20"
                  :iconColor="$color.coro['danger-500']"
                />
              </span>
            </v-col>
          </v-row>
          <v-row class="mb-2" v-if="anyMedicationClassSelected">
            <v-col cols="12">
              <h4>{{ $ucfirst($t("medication.relatedParameters")) }}</h4>
            </v-col>
            <v-col cols="12" v-if="relatedParameters && relatedParameters.length">
              <v-chip
                class="mr-2 mb-2"
                pill
                v-for="param in relatedParameters"
                v-bind:key="param.key"
              >
                <related-parts-icon :categoryKey="param.key" class="mr-2" />
                {{ param.name }}
              </v-chip>
            </v-col>
            <v-col cols="12" v-else>
              {{ $ucfirst($t('general.noneAnswer', { catalog: 'app_mobile' })) }}
            </v-col>
          </v-row>
          <v-row v-if="reasons && reasons.length" class="mb-2">
            <v-col cols="12">
              <h4>{{ $ucfirst($t("questionnaires.HCRU.questions.Q5.TABLE_Q5_OTHER_COSTS.header.col2", {catalog: "questionnaires"})) }}</h4>
            </v-col>
            <v-col
              v-for="reason in reasons"
              cols="12"
              v-bind:key="reason"
            >
              {{ reason }}
            </v-col>
          </v-row>
          <v-row v-if="auditTrailData">
            <v-col cols="12">
              <h4>{{ $ucfirst($t("dss.changeHistory")) }}</h4>
            </v-col>
            <v-col cols="12">
              <v-simple-table
                fixed-header
                class="v-patient-overview-medication-prescription__audit-trail-table scrollbar-visible"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">
                        {{ $ucfirst($t("medication.when")) }}
                      </th>
                      <th class="text-left">
                        {{ $ucfirst($t("medication.who")) }}
                      </th>
                      <th class="text-left">
                        {{ $ucfirst($t("medication.what")) }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, i) in auditTrailData" :key="i">
                      <td>{{ item.node.when | moment }}</td>
                      <td>{{ $ucfirst(item.node.who) }}</td>
                      <td>{{ item.node.what }}</td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <div
                v-if="deferMedicationClassAssets.length"
                class="d-flex justify-center flex-wrap mt-3"
              >
                <img
                  v-for="asset in deferMedicationClassAssets" :key="asset.key"
                  :src="asset.path"
                  :alt="asset.name"
                  @click="selectedAsset = asset"
                  class="ma-2 v-patient-overview-medication-prescription__infographic"
                >
              </div>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12">
              <h4>{{ $ucfirst($t("medication.notes")) }}</h4>
            </v-col>
            <v-col>
              <textarea
                class="
                  v-patient-overview-medication-prescription__notes-textarea
                "
                v-model="debouncedNote"
                v-bind:class="{ disabled: selectedMedicationItem.schedule.wasDeletedByTypesSinceLastSDM }"
                :disabled="selectedMedicationItem.schedule.wasDeletedByTypesSinceLastSDM"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col
          class="
            lg-6
            sm-12
            ma-2
            py-3
            px-5
            border
            bg-color-coro-lightgray
            v-patient-overview-medication-prescription__click-on-prescription
            flex-grow-1
          "
          v-else
        >
          <p>
            {{
              $ucfirst(
                $t("mobileAppHelp.myMedicationOverviewQuartileOfTheDay5", {
                  catalog: "content",
                })
              )
            }}
          </p>
        </v-col>
      </v-row>
    </div>
    <v-overlay v-if="selectedAsset">
      <img
        :src="selectedAsset.path"
        :alt="selectedAsset.name"
        class="v-patient-overview-medication-prescription__infographic-full"
      >
      <v-btn
        text
        @click="selectedAsset = null"
        class="v-patient-overview-medication-prescription__infographic-close-btn ignoredisableall"
        color="black"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-overlay>
  </div>
</template>

<script>
import SmartIcon from "../../../components/Parts/SmartIcon.vue";
import MedicationRow from "../../../components/Parts/MedicationRow";
import gql from "graphql-tag";
import moment from "moment";
import RelatedPartsIcon from "../../../components/Parts/RelatedPartsIcon.vue";
import { debounce } from "debounce";
import { mapGetters } from "vuex";
import OpenDSSButton from '../../../components/Parts/OpenDSSButton.vue';

/**
 * Component PatientOverviewMedicationPrescription.vue
 *
 * @module PatientOverviewMedicationPrescription
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.6] app/src/APP_DASHBOARD/templates/views/Patient/Medication/PatientOverviewMedicationPrescription.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "PatientOverviewMedicationPrescription",
  components: {
    SmartIcon,
    MedicationRow,
    RelatedPartsIcon,
    OpenDSSButton
  },
  data: function () {
    return {
      medicationSchedules: null,
      medicationClasses: [],
      selectedMedicationItem: null,
      auditTrailData: null,
      reasons: [],
      relatedParameters: null,
      note: "",
      selectedAsset: null,
      medicationClassAssets: [],
      deferMedicationClassAssets: [],
      savingNoteId: null
    };
  },
  filters: {
    moment: function (date) {
      return date ? moment(date).format("DD/MM/YYYY") : "";
    },
  },
  mounted: function () {
    this.loadData();
    this.getMedicationClasses()
    this.loadAssets()
  },
  computed: {
    ...mapGetters({
      patientId: "patientSummary/id",
      currentLanguage: "getCurrentLanguage",
      hasReadOnlyAccess: "user/hasReadOnlyAccess"
    }),
    updateNoteDebounced() {
      const id = this.selectedMedicationItem.schedule.id
      return debounce((value) => this.updateNote(value, id), 800);
    },
    debouncedNote: {
      get() {
        return this.note;
      },
      set(value) {
        this.note = value;
        this.savingNoteId = this.selectedMedicationItem.schedule.id;
        this.updateNoteDebounced(value || "");
      },
    },
    BASE_URL() {
      return process.env.VUE_APP_API_ROOT_URL
    },
    allSelectedMedicationClasses() {
      if (this.medicationSchedules.length) {
        return this.medicationSchedules
          .filter(item => !item.deleted)
          .map(item => item.medication.classes).flat();
      } else {
        return []
      }
    },
    anyMedicationClassSelected() {
      return !!this.selectedMedicationItem?.schedule?.medication?.classes.length
    }
  },
  watch: {
    patientId: function () {
      this.loadData();
    },
    selectedMedicationItem: function (val) {
      this.loadAuditTrail();
      this.getRelatedParameters();
      this.getReasons();

      // Set note.
      this.note = val.schedule.notes || "";
    },
  },
  methods: {
  async getMedicationClassAssets() {
    if (!this.selectedMedicationItem) {
      return [];
    }
    const classes = this.selectedMedicationItem.schedule.medication.classes;
    const uniqueAssets = [];

    const assets = classes.map(async (item) => {
      const asset = this.medicationClassAssets.find(asset => asset.categories.includes(`MEDICATION_${item.key}`))

      if (asset && !uniqueAssets.includes(asset.name)) {
        uniqueAssets.push(asset.name);
        return {
          path: await this.$assetRemote(this.BASE_URL + asset.image, true),
          name: asset.name,
          key: item.key
        }
      }
      return ''
    })

    return Promise.all(assets);
  },
    loadData: function () {
      this.loadMyMedicationSchedule();
    },

    updateNote(value, id) {
      this.$app.apolloMobileClient
        .mutate({
          mutation: gql`
            mutation createUpdateMyMedicationSchedule($id: String!, $notes: String!, $patientId: String!){
              createUpdateMyMedicationSchedule(
                medicationSchedule: {
                  id: $id,
                  notes: $notes,
                  patientId: $patientId
                }
              ) {
                id
                notes
              }
            }
          `,
          variables: {
            id,
            notes: value,
            patientId: this.patientId,
          },
        })
        .then((result) => {
          const resultNote = result.data.createUpdateMyMedicationSchedule.notes;
          // Apply the change without refetching the medications.
          // Apply the change to whole array in case selectedMedicationItem has already changed
          const updatedSchedules = this.medicationSchedules.map(medicationSchedule => {
            if (medicationSchedule.id === id) {
              medicationSchedule.notes = resultNote;
            }
            return medicationSchedule
          });
          this.medicationSchedules = updatedSchedules;
          this.savingNoteId = null;
        });
    },

    getRelatedParameters: function () {
      let relatedParams = [];
      if (this.selectedMedicationItem.schedule.medication.classes.length) {

        this.selectedMedicationItem.schedule.medication.classes.forEach((c) => {
          if (c.parameters.length) {
            c.parameters.forEach((param) => {
              if (
                relatedParams.findIndex((item) => item.key === param.key) === -1
              ) {
                relatedParams.push(param);
              }
            });
          }
        });
      }
      this.relatedParameters = relatedParams;
    },

    getReasons: function () {
      this.reasons = [];
      if (this.selectedMedicationItem.schedule.medication.classes.length) {
        this.selectedMedicationItem.schedule.medication.classes.forEach((c) => {
          if (!this.reasons.includes(c.description)) {
            this.reasons.push(c.description);
          }
        });
        // console.log("REASONS", this.reasons);
      }
    },

    getMedicationClasses: function () {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            {
              medicationClasses {
                key
                name
                description
                # parameters {
                #   key
                #   name
                # }
              }
            }
          `,
        })
        .then((result) => {
          this.medicationClasses = result.data.medicationClasses;
        });
    },

    isSelectedMedication: function (schedule) {
      if (this.selectedMedicationItem) {
        if (this.selectedMedicationItem.schedule.id === schedule.id) {
          return true;
        }
      }

      return false;
    },

    activateMedication: function (data) {
      if (this.savingNoteId === data?.schedule?.id) {
        return;
      }
      // console.log("SELECTED", data);
      this.selectedMedicationItem = data;

      this.getMedicationClassAssets().then((result) => {
        this.deferMedicationClassAssets = result.filter(item => item);
      })
    },

    loadAuditTrail: function () {
      this.$app.apolloClient
        .query({
          query: gql`
            query auditTrails($patientId: String!, $medicationId: String!) {
              medicationScheduleAuditTrails(
                functions: [
                  { function: patientId, parameters: [$patientId] }
                ]
                conditions : {field : medicationScheduleId, value : $medicationId}
                # limit : 10
                orders: [{ field: when, direction: desc }]
              ) {
                edges {
                  node {
                    who
                    when
                    what
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
            medicationId: this.selectedMedicationItem.schedule.id,
          },
        })
        .then((result) => {
          // console.log("AUDIT TRAILS", result.data);
          this.auditTrailData = result.data.medicationScheduleAuditTrails.edges;
        });
    },

    loadMyMedicationSchedule: function () {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query myMedicationSchedules($patientId: String!) {
              myMedicationSchedules(
                functions: { function: patientId, parameters: [$patientId] }, # conditions: {
                orders: [
                  { function: newOrUpdatedSinceLastVisit, direction: desc },
                  { function: byPriority, direction: asc }
                ]
              ) {
                id
                notes
                deleted
                wasUpdatedByTypesSinceLastSDM: auditTrailSinceLastVisit(
                  types: ["UPDATE"]
                ) {
                  typeAsString
                }
                wasDeletedByTypesSinceLastSDM: auditTrailSinceLastVisit(
                  types: ["UPDATE"]
                  fields: ["deleted"]
                  newValue: "true"
                ) {
                  typeAsString
                }
                wasCreatedByTypesSinceLastSDM: auditTrailSinceLastVisit(
                  types: ["CREATE"]
                ) {
                  typeAsString
                }
                schedule {
                  id
                  note
                  intervals {
                    interval
                    intervalOn
                  }
                  monthsOfYear
                  daysOfMonth
                  daysOfWeek
                  weeksOfMonth
                  weeksOfYear
                  anchorDate
                  times
                }
                intakeMoments {
                  id
                  time
                  amount
                  reminder
                }
                medication {
                  medicationType
                  resourceType
                  id
                  name
                  classes {
                    key
                    parentKey
                    name
                    description
                    administration
                    parameters {
                      key
                      name
                    }
                  }
                  form {
                    text
                    coding {
                      system
                      version
                      code
                      display
                    }
                  }
                }
                dosage {
                  id
                  sequence
                  doseAndRate {
                    id
                    sequence
                    doseQuantity {
                      value
                      code
                    }
                  }
                  route {
                    text
                    coding {
                      system
                      version
                      code
                      display
                    }
                  }
                }
              }
            }
          `,
          variables: {
            patientId: this.patientId,
          },
        })
        .then((result) => {
          // console.log(result);
          // console.log('ONLY CARDIO', result.data.myMedicationSchedules.filter(item => item.medication.extension[0].value == 'cardio'));

          // Only Cardio medication.
          this.medicationSchedules = result.data.myMedicationSchedules.filter(
            (item) => {
              return item.medication.medicationType === "cardio";
            }
          );

          this.updateSelectedMedicationItem();
        });
    },

    async printPrescription(type) {
      const success = await this.$app.patientManager.printPrescription(
        type,
        this.patientId
      );

      if (success) {
        this.$toasted.show(this.$ucfirst(this.$t('dss.sentToPrinter')), {
          type : 'success',
          icon : 'check',
          position: 'bottom-center',
        });
      }
    },

    updateSelectedMedicationItem() {
      const selected = this.medicationSchedules.find(item => item.id === this.selectedMedicationItem?.schedule?.id)
      if (selected) {
        this.selectedMedicationItem = {...this.selectedMedicationItem, schedule: selected}
        // update assets
        this.getMedicationClassAssets().then((result) => {
          this.deferMedicationClassAssets = result.filter(item => item);
        })
      }
    },

    updateClasses() {
      this.loadMyMedicationSchedule();
    },

    loadAssets() {
      this.$app.apolloMobileClient
        .query({
          query: gql`
            query{
                documents(
                  conditions: { gate: and, inputs: [{field: categoryKey, value: "MEDICATION_CLASS"}] }
                ){
                  title
                  categories {
                    key
                    type
                  }
                  parts {
                    ... on ImagePart {
                      image {
                        file {
                          hasFile
                          webPath
                        }
                      }
                    }
                  }
                }
              }
          `,
        })
        .then((result) => {
          const data = result?.data?.documents
          if (!data.length) {
            return;
          }
          const files = data.map(item => ({
            categories: item.categories.filter(cat => cat.type === 'MEDICATION').map(cat => cat.key),
            name: item.title,
            image: item.parts[0].image.file.webPath
          }))

          this.medicationClassAssets = files;
        });
    }
  },
};
</script>


<style lang="scss">
@import "../../../../../scss/colors";

.v-patient-overview-medication-prescription__open-decision-support {
  div.inline-block {
    &:hover {
      opacity: 0.5;
      cursor: pointer;
    }
  }
}

.v-patient-overview-medication-prescription__current-prescription {
  font-weight: 500;
  font-family: Futura;
  color: map-get($coroColors, gray);
}

.v-patient-overview-medication-prescription__click-on-prescription {
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}

.v-patient-overview-medication-prescription__close-icon {
  &:hover i {
    opacity: 0.5;
    cursor: pointer;
  }
}

.v-patient-overview-medication-prescription__notes-textarea {
  width: 100%;
  border: 1px solid black;
  padding: 16px;
  min-height: 140px;
  font-size: 1rem;

  &.disabled {
    background-color: map-get($coroColors, lightgray);
  }
}

.v-patient-overview-medication-prescription__audit-trail-table {
  max-height: 300px;
  overflow: auto;

  table > thead > tr > th,
  table > tbody > tr > td {
    font-size: 1rem !important;
    line-height: 1.6 !important;
  }
}

.v-patient-overview-medication-prescription__infographic-close-btn {
    position: absolute;
    top: 0;
    right: 0;
  }

.v-patient-overview-medication-prescription__infographic-full {
  max-width: 90vw;
  max-height: 90vh;
  width: 100%;
}

.v-patient-overview-medication-prescription__infographic {
  max-width: 250px;
  width: 100%;

  &:hover {
    cursor: pointer;
  }
}
</style>
